import { useState, useRef, useMemo } from 'react';
import { EventInput } from '@amzn/red-velvet-api';
import { WritingAssistantState, MIN_AI_DESCRIPTION_LENGTH } from '../../utils/aiContentTypes';
import { useRewriteEventDescription, useGenerateShortDescription } from './aiContent';
import { markdownToHtml, stripMarkdown } from '../../utils/markdownToHtml';
import { stripHtml } from '../../utils/htmlText';
import { FIELD_LIMITS } from '../../utils/fieldConstraints';
import { publishKatalMetric } from '../../components/katalAnalytics';

interface UseWritingAssistantProps {
  eventDescription: string;
  setEventDescription: (html: string) => void;
  summarizedDescription: string;
  setSummarizedDescription: (value: string) => void;
  currentFields: EventInput;
  updateFieldHandler: (fields: EventInput) => void;
  metricPrefix: string;
  /** Available during edit-event flow; undefined while creating a new event. */
  eventId?: string;
}

export function useWritingAssistant({
  eventDescription,
  setEventDescription,
  summarizedDescription,
  setSummarizedDescription,
  currentFields,
  updateFieldHandler,
  metricPrefix,
  eventId,
}: UseWritingAssistantProps) {
  const [assistantState, setAssistantState] = useState<WritingAssistantState>('idle');
  const [rewrittenDescription, setRewrittenDescription] = useState('');
  const [isRewriteValid, setIsRewriteValid] = useState(true);
  const [tips, setTips] = useState('');
  const [assistantError, setAssistantError] = useState<string | undefined>();
  const [isShortDescAiGenerated, setIsShortDescAiGenerated] = useState(false);
  const [shortDescError, setShortDescError] = useState(false);
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  const [editorWidthPct, setEditorWidthPct] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);

  const fieldsRef = useRef({ eventDescription, summarizedDescription });
  fieldsRef.current = { eventDescription, summarizedDescription };
  const currentFieldsRef = useRef(currentFields);
  currentFieldsRef.current = currentFields;

  const rewriteMutation = useRewriteEventDescription();
  const shortDescMutation = useGenerateShortDescription();

  const descriptionPlainTextLength = useMemo(
    () => stripHtml(eventDescription).length,
    [eventDescription]
  );

  const canRewrite =
    descriptionPlainTextLength >= MIN_AI_DESCRIPTION_LENGTH &&
    descriptionPlainTextLength <= FIELD_LIMITS.EVENT_DESCRIPTION;

  // Tooltip explaining why the user cannot rewrite (undefined when rewrite is allowed)
  const rewriteDisabledReason = canRewrite
    ? undefined
    : descriptionPlainTextLength > FIELD_LIMITS.EVENT_DESCRIPTION
      ? 'writingAssistant.maxCharsHint'
      : 'writingAssistant.minCharsHint';

  const handleRewrite = () => {
    const plainText = stripHtml(eventDescription);
    if (
      plainText.length < MIN_AI_DESCRIPTION_LENGTH ||
      plainText.length > FIELD_LIMITS.EVENT_DESCRIPTION
    )
      return;
    setAssistantState('loading');
    setAssistantError(undefined);
    setIsRewriteValid(true);
    publishKatalMetric(`${metricPrefix}_aiRewriteRequested`, String(plainText.length));
    rewriteMutation.mutate(
      { description: plainText, eventId },
      {
        onSuccess: (data) => {
          const rewritten = data.rewrittenDescription ?? '';
          // No flag means treat it as valid, so an older backend still works.
          const valid = data.valid !== false;
          setRewrittenDescription(rewritten);
          setIsRewriteValid(valid);
          setTips(data.tips ?? '');
          setAssistantState('result');
          if (valid) {
            publishKatalMetric(`${metricPrefix}_aiRewriteGenerated`, String(rewritten.length));
          } else {
            publishKatalMetric(`${metricPrefix}_aiRewriteRejected`, eventId ?? 'Draft event');
          }
        },
        onError: (error) => {
          setAssistantError(error.message);
          setAssistantState('error');
          publishKatalMetric(`${metricPrefix}_aiRewriteFailed`, error.message);
        },
      }
    );
  };

  const handleAcceptRewrite = () => {
    // Don't put a turned-down rewrite into the description, even if Accept is clicked.
    if (!isRewriteValid) return;
    const html = markdownToHtml(rewrittenDescription);
    setEventDescription(html);
    setAssistantState('tips');
    publishKatalMetric(`${metricPrefix}_aiRewriteAccepted`, eventId ?? 'Draft event');
    updateFieldHandler({
      ...currentFieldsRef.current,
      description: html,
      shortDescription: fieldsRef.current.summarizedDescription,
    });
  };

  const handleCancelRewrite = () => {
    setAssistantState('idle');
    setRewrittenDescription('');
    setIsRewriteValid(true);
    setTips('');
    publishKatalMetric(`${metricPrefix}_aiRewriteCancelled`, eventId ?? 'Draft event');
  };

  const triggerShortDescGeneration = (descriptionText?: string) => {
    const plainText = descriptionText ?? stripHtml(eventDescription);
    if (
      plainText.length < MIN_AI_DESCRIPTION_LENGTH ||
      plainText.length > FIELD_LIMITS.EVENT_DESCRIPTION
    )
      return;
    setShortDescError(false);
    // If we already generated a short description in this session, the user is
    // asking for a different one. Tell the server so it can vary the output.
    const regenerate = isShortDescAiGenerated;
    publishKatalMetric(`${metricPrefix}_aiGenerateShortDescRequested`, String(plainText.length));
    shortDescMutation.mutate(
      { description: plainText, eventId, regenerate },
      {
        onSuccess: (data) => {
          // Truncation is a safety measure to avoid exceeding the max character limit
          const shortDesc = stripMarkdown(data.shortDescription ?? '').slice(
            0,
            FIELD_LIMITS.SUMMARIZED_DESCRIPTION
          );
          setSummarizedDescription(shortDesc);
          setIsShortDescAiGenerated(true);
          updateFieldHandler({
            ...currentFieldsRef.current,
            description: fieldsRef.current.eventDescription,
            shortDescription: shortDesc,
          });
        },
        onError: (error) => {
          setShortDescError(true);
          publishKatalMetric(`${metricPrefix}_aiGenerateShortDescFailed`, error.message);
        },
      }
    );
  };

  const handleDescriptionChange = (html: string) => {
    setEventDescription(html);
    if (assistantState === 'tips') setAssistantState('idle');
  };

  const handleTogglePanel = () => {
    const next = !isPanelCollapsed;
    setIsPanelCollapsed(next);
    publishKatalMetric(`${metricPrefix}_aiPanel${next ? 'Collapsed' : 'Expanded'}`, '');
  };

  const clearAiGeneratedLabel = () => setIsShortDescAiGenerated(false);

  return {
    assistantState,
    rewrittenDescription,
    isRewriteValid,
    tips,
    assistantError,
    canRewrite,
    rewriteDisabledReason,
    isPanelCollapsed,
    editorWidthPct,
    containerRef,
    isShortDescAiGenerated,
    shortDescError,
    isGeneratingShortDesc: shortDescMutation.isPending,
    setEditorWidthPct,
    handleRewrite,
    handleAcceptRewrite,
    handleCancelRewrite,
    handleDescriptionChange,
    handleTogglePanel,
    triggerShortDescGeneration,
    clearAiGeneratedLabel,
  };
}
